import React from 'react';

import clsxm from '@/lib/clsxm';

export interface ChipOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  /** No voice would match: shown greyed out, unless it is the selection. */
  disabled?: boolean;
}

interface ChipGroupProps {
  options: ChipOption[];
  value: string;
  /** Called with '' when the selected chip is clicked again. */
  onChange: (value: string) => void;
}

const ChipGroup = ({ options, value, onChange }: ChipGroupProps) => (
  <div className='flex flex-wrap gap-1.5'>
    {options.map((option) => {
      const active = option.value === value;
      const disabled = Boolean(option.disabled) && !active;
      return (
        <button
          key={option.value}
          type='button'
          aria-pressed={active}
          disabled={disabled}
          onClick={() => onChange(active ? '' : option.value)}
          className={clsxm(
            'inline-flex h-8 items-center gap-1 rounded-full border px-2.5 text-[13px] font-medium transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
            active
              ? 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-gray-900'
              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:bg-transparent dark:text-slate-300 dark:hover:border-white/20 dark:hover:bg-white/5',
            disabled &&
              'cursor-not-allowed opacity-40 hover:border-gray-200 hover:bg-white dark:hover:border-white/10 dark:hover:bg-transparent',
          )}
        >
          {option.icon && (
            <span
              className={clsxm(
                'flex shrink-0 items-center',
                active
                  ? 'text-white/70 dark:text-gray-500'
                  : 'text-gray-400 dark:text-slate-500',
              )}
            >
              {option.icon}
            </span>
          )}
          {option.label}
        </button>
      );
    })}
  </div>
);

export default ChipGroup;
